// listeners that mostly dispatch other listeners

import { type Locator } from "@readium/shared"
import { isAnyOf } from "@reduxjs/toolkit"

import { getApiUrlFromResourceHref } from "@/reader/BookService"
import { readerPlayer } from "@/services/AudioPlayerService"
import {
  type SkipPartButtonPayload,
  navItemPressed,
  skipPartButtonHeld,
  skipPartButtonPressed,
  userRequestedTextNavigation,
} from "@/store/actions"
import {
  selectCurrentBook,
  selectCurrentLocator,
  selectCurrentToCLocator,
  selectReadingMode,
} from "@/store/slices/readingSessionSlice"

import { handleChapterSkip, handleFragmentSkip } from "./helpers"
import { startAppListening } from "./listenerMiddleware"

// shared locators
const matchSharedLocators = isAnyOf(navItemPressed)

startAppListening({
  matcher: matchSharedLocators,
  effect: async (action, listenerApi) => {
    const mode = selectReadingMode(listenerApi.getState())
    const currentBook = selectCurrentBook(listenerApi.getState())
    if (!currentBook) return

    if (mode === "audiobook") {
      const currentTrackIndex = readerPlayer()
        .getQueue()
        .findIndex(
          (track) =>
            track.url ===
            getApiUrlFromResourceHref(
              currentBook.uuid,
              action.payload.locator.href,
              "listen",
            ),
        )

      const time = action.payload.locator.locations.fragments[0]?.split("=")[1]
      const position = time ? parseFloat(time) : 0
      await readerPlayer().skip(currentTrackIndex, position)
      return
    }

    readerPlayer().pause()
    listenerApi.dispatch(
      userRequestedTextNavigation({ locator: action.payload.locator }),
    )
    return
  },
})

// handle skip buttons (press = short skip, hold = chapter/track skip)
startAppListening({
  matcher: isAnyOf(skipPartButtonPressed, skipPartButtonHeld),
  effect: async (action, listenerApi) => {
    const mode = selectReadingMode(listenerApi.getState())
    const currentTocItem = selectCurrentToCLocator(listenerApi.getState())
    const { direction, context } = (
      action as unknown as { payload: SkipPartButtonPayload }
    ).payload

    const isPressed = action.type === skipPartButtonPressed.type
    const isHeld = action.type === skipPartButtonHeld.type

    // in miniplayer or audiobook modes, buttons control audio directly
    if (context === "miniplayer" || mode === "audiobook") {
      if (isPressed) {
        const seekAmount = direction === "next" ? 15 : -15
        readerPlayer().seekBy(seekAmount)
        return
      }

      if (isHeld) {
        if (direction === "next") {
          await readerPlayer().skipToNext()
        } else {
          await readerPlayer().skipToPrevious()
        }
        return
      }
    }

    // in readaloud mode, buttons navigate text (audio follows via sync)
    const currentLocator = selectCurrentLocator(listenerApi.getState())
    const requestTextNavigation = (locator: Locator) => {
      listenerApi.dispatch(userRequestedTextNavigation({ locator }))
    }
    if (isPressed) {
      await handleFragmentSkip(direction, currentLocator, requestTextNavigation)
      return
    }

    if (isHeld) {
      handleChapterSkip(
        direction,
        currentTocItem ?? null,
        requestTextNavigation,
      )
      return
    }
  },
})
